import { css } from '@lastbrand/web-ui-styled-system/css'

/* eslint-disable i18next/no-literal-string -- Panda tokens / CSS literals, not user-facing copy */

// Carousel root — full-width; `position: relative` anchors the absolutely-positioned arrows
// (mirrors legacy `.sliderArrows { position: relative }`).
export const carouselRootCss = css.raw({
  width: 'full',
  position: 'relative',
})

// Flex track (CarouselContent's inner `carousel-content` element, already `display: flex`).
// Slide content is capped at `100rem` (1600px) via `sectionWrapperClass`, so on ultrawide
// viewports the track has free space — center the capped banner instead of left-aligning it.
// When the slide(s) fill/overflow the track there's no free space, so this is a no-op.
export const carouselContentCss = css.raw({
  justifyContent: 'center',
})

// Per-slide content wrapper — faithful to `heroCarousel.module.scss .sectionWrapper`, which sets ONLY a
// max-width (no width, no auto-margin → content is left-aligned, not centered). The legacy caps (144rem
// desktop / 37.5rem mobileOnly) are measured against a *fluid* root font-size set only on the legacy path
// (`normalize.scss`: 0.6944vw desktop / 2.6667vw mobile, clamped to 11.1104px above a 1600px viewport), so
// `144rem` ≈ 100vw below 1600px and ≈ 1600px above it; `37.5rem` ≈ 100vw on mobile. The DS path uses the
// browser-default 16px root, so a single `maxWidth: 100rem` (1600px) reproduces BOTH: capped at 1600px on
// ultrawide, uncapped (full content width) below it — including mobile. No DS size token for 1600px → base-16
// rem literal (1600/16 = 100rem). Left-aligned (no marginInline) to match legacy.
export const sectionWrapperClass = css({
  maxWidth: '100rem',
})

// Per-slide width — PARITY with legacy `.swiperSlide { width: auto !important }`: slides size to content
// (the CarouselItem recipe ships `flex: 0 0 100%`; a static `flex` here is the 2nd css() arg so it wins).
// NOTE: rendered width is therefore content-driven — verify visually per banner.
export const carouselItemCss = css.raw({
  flex: '0 0 auto',
})

// Prev/next arrows — PARITY with legacy `swiper.module.scss`: bare directional arrows pinned to the
// bottom-right corner, visible on ALL viewports (legacy `showNavigator` defaulted true, no `pointer`/
// breakpoint gate), dimmed (not hidden) at the scroll boundaries. Legacy positions are rem against the
// fluid root → effective px at the reference widths: bottom 2rem ≈ 20px (space-20), next right 2rem ≈ 20px
// (space-20), prev right 8rem ≈ 80px (space-80), icon 4rem ≈ 40px. No circular background/shadow (legacy
// had none — just a black-stroked arrow).
//
// IMPORTANT: `CarouselPreviousButton`/`NextButton` always merge their built-in `carouselArrowButtonStyles`
// (`css(carouselArrowButtonStyles, cssProp)`) onto the element — even with `asChild`. That recipe adds DS
// interaction states legacy never had (`_hover` → gray, `_active` → mediumgray, `_disabled` → silvergray).
// To stay faithful to legacy (static black arrow, only dimmed when disabled) we override those states back
// to `black` here; our object is the 2nd css() arg so it wins on conflicts.
const arrowBaseCss = {
  position: 'absolute',
  bottom: 'space-20',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  border: 'none',
  background: 'transparent',
  color: 'black',
  cursor: 'pointer',
  _hover: { color: 'black' },
  _active: { color: 'black' },
  // Legacy `.navIcon__disabled`: black at opacity 0.2 + not-allowed at isBeginning / isEnd
  // (overrides the built-in recipe's silvergray disabled color).
  _disabled: {
    color: 'black',
    opacity: 0.2,
    cursor: 'not-allowed',
  },
} as const

export const carouselPrevArrowCss = css.raw({ ...arrowBaseCss, right: 'space-80' })
export const carouselNextArrowCss = css.raw({ ...arrowBaseCss, right: 'space-20' })

/* eslint-enable i18next/no-literal-string */
